import { ConfigProvider, Spin } from 'antd';
import { Dispatch, SetStateAction, useState } from 'react';

import { SlimTable, TableWrapper } from '../../../../../../components/index';
import {
  formatNumberToLocale,
  isNullOrUndefined,
} from '../../../../../../utils/utils';
import { columns } from './columns';
import { useGetMerchantsCeGroupbyNcmItemsQuery } from '../../../../../../services/ncmItemApi';
import { useChangePagination } from '../../../../../../hooks/useChangePagination';
import { NcmItemFilters } from '../../../../../../types';

type AssignLoadingTableProps = {
  manifestCode: string;
  stopoverId: number;
  operatorId: number;
  operatorCompanyId: number;
  totalWeight: number;
  totalItems: number;
  cargosNotAssigned?: boolean;
  isLoading?: boolean;
  rowKey?: string;
  onSelectItem: (e: boolean, record: any) => void;
  rowSelection?: any;
  hasCheckboxColumn: boolean;
  isVehicleManifest: boolean;
  ncmIds?: number[];
  setNcmIdsToEdit?: Dispatch<SetStateAction<number[]>> | undefined;
  setNcmItemFilters?: Dispatch<SetStateAction<NcmItemFilters[]>>;
  ncmItemFilters?: NcmItemFilters[];
};

export function AssignLoadingTable(props: AssignLoadingTableProps) {
  const {
    manifestCode,
    stopoverId,
    operatorId,
    operatorCompanyId,
    totalWeight,
    totalItems,
    cargosNotAssigned,
    isLoading,
    onSelectItem,
    rowKey,
    rowSelection,
    hasCheckboxColumn,
    isVehicleManifest,
    ncmIds,
    setNcmIdsToEdit,
    setNcmItemFilters,
    ncmItemFilters,
  } = props;

  const [checkedAll, setCheckedAll] = useState(false);

  const PAGE_SIZE = 30;

  const { onChangePagination, queryPage } = useChangePagination();

  const ncmItemsParams = {
    manifest_code: manifestCode,
    operator_id: operatorId,
    stopover_id: stopoverId,
    operator_company_id: operatorCompanyId,
    ncm_item_assigned: true,
  } as NcmItemFilters;

  const { data: ncmItemsData, isFetching: isFetchingNcmItemsData } =
    useGetMerchantsCeGroupbyNcmItemsQuery(
      {
        ...ncmItemsParams,
        cargo_assigned: cargosNotAssigned ? false : '',
        page: onChangePagination(),
        page_size: PAGE_SIZE,
      },
      { skip: isNullOrUndefined(stopoverId), refetchOnMountOrArgChange: true }
    );

  function addNcmItemParams(isChecked: boolean) {
    if (ncmItemFilters && setNcmItemFilters) {
      const itemIndex = ncmItemFilters.findIndex(
        (item) =>
          item.manifest_code === ncmItemsParams.manifest_code &&
          item.operator_id === ncmItemsParams.operator_id &&
          item.stopover_id === ncmItemsParams.stopover_id &&
          item.operator_company_id === ncmItemsParams.operator_company_id
      );

      setNcmItemFilters((prevState) =>
        itemIndex !== -1 && !isChecked
          ? prevState.filter((_, index) => index !== itemIndex)
          : itemIndex === -1 && isChecked
          ? [...prevState, { ...ncmItemsParams, total_items: totalItems }]
          : prevState
      );
    }
  }

  function handleCheckedNcmItem(ncm_item_id: number) {
    return !!ncmIds?.find((id) => id === ncm_item_id);
  }

  function handleCheckedAllManifestNcmItems(isChecked: boolean) {
    setCheckedAll(isChecked);
    addNcmItemParams(isChecked);
  }

  function customizeRenderEmpty() {
    return <>Não há dados cadastrados.</>;
  }

  function getTableSummary() {
    return (
      <SlimTable.Summary fixed>
        <SlimTable.Summary.Row key="row_0">
          <SlimTable.Summary.Cell index={0} colSpan={2} key={0} align="left">
            TOTAIS: {totalItems || 0}
          </SlimTable.Summary.Cell>
          {!isVehicleManifest && (
            <SlimTable.Summary.Cell colSpan={5} index={1} key={1} align="right">
              {formatNumberToLocale(totalWeight)} ton
            </SlimTable.Summary.Cell>
          )}
        </SlimTable.Summary.Row>
      </SlimTable.Summary>
    );
  }

  return (
    <Spin spinning={isFetchingNcmItemsData} tip="Carregando...">
      <TableWrapper>
        <ConfigProvider renderEmpty={customizeRenderEmpty}>
          <SlimTable
            rowKey={rowKey || 'key'}
            columns={columns(
              onSelectItem,
              hasCheckboxColumn,
              isVehicleManifest,
              checkedAll,
              handleCheckedAllManifestNcmItems,
              handleCheckedNcmItem
            )}
            dataSource={ncmItemsData?.results || []}
            rowSelection={rowSelection}
            className="read-only"
            pagination={{
              position: ['bottomCenter'],
              pageSize: PAGE_SIZE,
              defaultPageSize: PAGE_SIZE,
              total: totalItems,
              onChange: onChangePagination,
              showSizeChanger: false,
              current: queryPage,
            }}
            summary={() => getTableSummary()}
            scroll={{ x: '100%' }}
          />
        </ConfigProvider>
      </TableWrapper>
    </Spin>
  );
}
